import { createTheme, ThemeProvider } from "@mui/material";
import React, { useMemo } from "react";
import { useSelector } from "react-redux";

const getFontOffset = (fontSize) => {
  switch (fontSize) {
    case "small":
      return -2;
    case "large":
      return 2;
    case "extra_large":
      return 4;
    default:
      return 0;
  }
};

export default function AppThemeProvider({ children }) {
  const user = useSelector((state) => state.auth.user);

  const disableAnimation =
    user?.default_animation === "true" || user?.default_animation === true;
  const fontSize = user?.font_size || "medium";

  const theme = useMemo(() => {
    const fontOffset = getFontOffset(fontSize);

    let baseTheme = createTheme({
      palette: {
        primary: {
          main: "#1D4ED8",
          dark: "#2563EB",
        },
        error: {
          main: "#E12121",
          light: "#FEEBEB",
          red300: "#FFC9C9",
          red200: "#FFE3E3",
          red900: "#BC1C1C",
        },
        background: {
          white: "#FFFFFF",
          lightWhite: "#FFFFFF33",
          backdropBg: "#1f1f1feb",
          darkWhite: "#F9F9F9",
          yellow: "#EC980C",
          loyal: "#FF9B3F",
          glassBg: "#EFEFEF85",
          gray100: "#FDFDFD",
          gray400: "#ECECED",
          gray300: "#F1F1F1",
          gray500: "#E6E6E6",
          gray600: "#DCDCDE",
          gray900: "#7F7D83",
          gray200: "#F8F8F8",
          blue200: "#EFF6FF",
          blue300: "#DFE9FF",
          blue400: "#BFDBFE",
          blue600: "#60A5FA",
          blue700: "#3B82F6",
          progressgray: "#ECF0F7",
          green900: "#018030",
          green: "#5cb85c",
          errorBlack: "#171717",
          zinc900: "#18181B",
          akiraRed: "#E12121",
          akiraRed100: "#e123231a",
          darkOrange: "#a16207",
          darkOrange100: "#a261071a",
          wayBeyond: "#e8eaed",
          coralRed10: "#FF3F421A",
          coralRed30: "#FF3F424D",
        },
        button: {
          // Dark blue background
          darkBlue: "#194774",
          // White button
          white: "#FFFFFF",
          // Orange button
          orange: "#FD7D12",
          // Orange button
          red: "#FF4545",
          // light green button
          lightgreen: "#4AD796",
          black: "#000000",
        },
        // error: {
        //   main: "#FF4E51",
        // },
        statusPill: {
          green: "#52D274",
          orange: "#F68633",
          black: "#222736",
          blue: "#2246B3",
          lightBlue: "#84B1F6",
        },
        input: {
          dark: "#25335E",
          light: "#FFFFFF",
        },
        inputBorder: {
          dark: "#25335E",
          light: "#FFFFFF",
        },
        buttonBorder: {
          dark: "#25335E",
          light: "#FFFFFF",
        },
        text: {
          black: "#000000",
          black40: "#00000040",
          white: "#FFFFFF",
          darkBlack: "#0A090B",
          primary: "#2563EB",
          orange: "#FD7D12",
          contrast: "#708090",
          error: "#FF3E3E",
          ottomanRed: "#EC221F",
          green: "#001400",
          gray: "#333333",
          darkgray: "#2D2B32",
          darkblue: "#0A1E41",
          gray600: "#475467",
          gray500: "#ADACB0",
          gray1200: "#1D1C20",
          darkgolden: "#5D512D",
          lightgray: "#4F4D55",
          neutral: "#2D333C",
          darkneutral: "#444D5A",
          lightred: "#F9A3A3",
          dark: "#1F1F1F",
          gray900: "#7F7D83",
          gray800: "#A3A2A6",
          gray700: "#344054",
          blue1300: "#051029",
          green800: "#019939",
          gray1400: "#808080",
          blue1000: "#1E3A8A",
          fashionGray: "#8b8b8b",
          newGray: "#A3A2A6",
        },
        sliderText: {
          main: "#000000",
          light: "#817D86",
        },
      },
      typography: {
        fontFamily: "Inter",
        48: { fontSize: 48 + fontOffset },
        36: { fontSize: 36 + fontOffset },
        32: { fontSize: 32 + fontOffset },
        30: { fontSize: 30 + fontOffset },
        28: { fontSize: 28 + fontOffset },
        26: { fontSize: 26 + fontOffset },
        24: { fontSize: 24 + fontOffset },
        22: { fontSize: 22 + fontOffset },
        20: { fontSize: 20 + fontOffset },
        18: { fontSize: 18 + fontOffset },
        16: { fontSize: 16 + fontOffset },
        14: { fontSize: 14 + fontOffset },
        12: { fontSize: 12 + fontOffset },
        10: { fontSize: 10 + fontOffset },
      },
      components: {
        MuiOutlinedInput: {
          styleOverrides: {
            input: {
              maxHeight: "48px",
              "&::placeholder": {
                color: "#A3A2A6 !important",
                opacity: 1,
              },
              "&[type=number]": {
                MozAppearance: "textfield",
              },
              "&[type=number]::-webkit-outer-spin-button, &[type=number]::-webkit-inner-spin-button":
                {
                  WebkitAppearance: "none",
                  margin: 0,
                },
              fontFamily: "Inter !important",
            },
            root: {
              variants: [
                {
                  props: { variant: "light" },
                  style: {
                    borderRadius: "8px",
                    backgroundColor: "#fff !important",
                  },
                },
                {
                  props: { variant: "otpField" },
                  style: {
                    py: "5px",
                    fontSize: "24px",
                    fontWeight: "700",
                    color: "#000000ff",

                    outline: "none !important",
                    flexGrow: 1,
                    flexShrink: 1,
                    maxWidth: "50px",
                    maxHeight: "50px",
                    height: "100%",
                    background: "#fff",
                    width: "100%",
                    borderRadius: "8px",
                    textAlign: "center",
                    "& .MuiOutlinedInput-input": { textAlign: "center" },
                    "& .MuiInputBase-input": {
                      paddingTop: "13px",
                      paddingBottom: "13px",
                    },
                    "& .MuiOutlinedInput-notchedOutline": {
                      border: "2px solid #E6E6E6",
                      borderRadius: "8px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#1D4ED8",
                        boxShadow: "0px 0px 0px 2px #DBEAFE",
                      },
                      // Error state
                      "&.Mui-error .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#E12121",
                        boxShadow: "0px 0px 0px 2px #FFC9C9",
                      },

                      // Error + Focus (keep error priority)
                      "&.Mui-focused.Mui-error .MuiOutlinedInput-notchedOutline":
                        {
                          borderColor: "#1D4ED8",
                          boxShadow: "0px 0px 0px 2px #DBEAFE",
                        },
                    },
                  },
                },
              ],
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              backgroundColor: "#FFFFFF",
              maxHeight: "48px",
              marginTop: "8px",
              borderRadius: "8px !important", // fallback border radius if no variant is matched
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none !important", // Removes default border
              },
              // "&:hover .MuiOutlinedInput-notchedOutline": {
              //   border: "none !important", // No border on hover
              // },
              // "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
              //   border: "2px solid rgba(253, 125, 18, 0.5) !important", // Keeps focus border
              // },
              "&[data-variant='basic']": {
                borderRadius: "8px",
              },
              "&[data-variant='plain']": {
                borderRadius: "5px",
              },
            },
          },
        },
        MuiButton: {
          styleOverrides: {
            root: {
              textTransform: "none",
              "&.Mui-disabled": {
                color: "#fff !important",
                backgroundColor: "#0000001f !important",
              },
              variants: [
                {
                  props: { variant: "contained" },
                  style: {
                    width: "100%",
                    backgroundColor: "#2563EB",
                    color: "#fff",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    fontWeight: "600",
                    border: "2px solid transparent",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      // border: "2px solid #fff",
                      backgroundColor: "#2563EB",
                      transform: "translateY(-2px)",
                      boxShadow:
                        "0px 10px 15px 0px #0000001A, 0px 4px 6px 0px #0000001A",
                    },
                  },
                },
                {
                  props: { variant: "blueContained" },
                  style: {
                    backgroundColor: "#2563EB",
                    color: "#FFFFFF",
                    borderRadius: "8px",
                    p: "8px 12px",
                    fontWeight: "500",
                    border: "1px solid transparent",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      backgroundColor: "#1e4fbc",
                      transform: "translateY(-2px)",
                      boxShadow:
                        "0px 10px 15px 0px #0000001A, 0px 4px 6px 0px #0000001A",
                    },
                  },
                },
                {
                  props: { variant: "whiteContained" },
                  style: {
                    backgroundColor: "#FFFFFF",
                    color: "#1D4ED8",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    fontWeight: "500",
                    border: "1px solid #60A5FA",
                    transition: "all 0.3s ease-in-out",
                    "&:hover": {
                      // border: "2px solid #1D4ED8",
                      transform: "translateY(-2px)",
                      boxShadow:
                        "0px 10px 15px 0px #0000001A, 0px 4px 6px 0px #0000001A",
                    },
                    "&.Mui-disabled": {
                      color: "#fff !important",
                      backgroundColor: "#2563EB !important",
                      border: "none",
                      opacity: 0.5,
                    },
                  },
                },
                {
                  props: { variant: "redContained" },
                  style: {
                    backgroundColor: "#BC1C1C",
                    color: "#FFFFFF",
                    borderRadius: "8px",
                    padding: "8px 12px",
                    fontWeight: "500",
                    "&:hover": {
                      backgroundColor: "#E12121",
                    },
                  },
                },
              ],
            },
          },
        },
        MuiTextField: {
          styleOverrides: {
            root: {
              "&.MuiTextField-root": {
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                },
              },
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              borderBottomStyle: "solid",
              borderBottomWidth: "1px",
              borderBottomColor: "#ECECED",
            },
          },
        },
        MuiAlert: {
          styleOverrides: {
            standardSuccess: {
              color: "#171717",
              alignItems: "center",
              borderRadius: "4px",
            },
            standardError: {
              color: "#E12121",
              alignItems: "center",
              borderRadius: "4px",
              boxShadow: "0px 7px 9px -2px rgba(255,0,0, 0.3)",
            },
            standardWarning: {
              alignItems: "center",
              borderRadius: "4px",
              color: "orange",
            },
            standardInfo: {
              alignItems: "center",
              borderRadius: "4px",
              color: "grey",
            },
          },
        },
        // MuiLinearProgress: {
        //   styleOverrides: {
        //     root: {
        //       variants: [
        //         {
        //           props: { variant: "success" },
        //           style: {
        //             position: "absolute",
        //             top: 0,
        //             left: 0,
        //             width: "100%",
        //             height: "5px !important",
        //             animation: `progressAnimation 5000ms linear forwards`,
        //             "@keyframes progressAnimation": {
        //               "0%": { width: "100%" },
        //               "100%": { width: "0%" },
        //             },
        //             "& .MuiLinearProgress-bar": {
        //               backgroundColor: "#171717 !important",
        //             },
        //           },
        //         },
        //         {
        //           props: { variant: "error" },
        //           style: {
        //             position: "absolute",
        //             top: 0,
        //             left: 0,
        //             width: "100%",
        //             height: "5px !important",
        //             animation: `progressAnimation 5000ms linear forwards`,
        //             "@keyframes progressAnimation": {
        //               "0%": { width: "100%" },
        //               "100%": { width: "0%" },
        //             },
        //             "& .MuiLinearProgress-bar": {
        //               backgroundColor: "red !important",
        //             },
        //           },
        //         },
        //       ],
        //     },
        //   },
        // },
        MuiTypography: {
          styleOverrides: {
            root: {
              // color: "#000 !important",
              fontFamily: "Inter !important",
              textOverflow: "ellipsis",
            },
          },
        },
        MuiSwitch: {
          styleOverrides: {
            root: {
              width: "100%",
              // height: "100%",
              maxHeight: "20px !important",
              maxWidth: "36px !important",
              padding: 0,
              "& .MuiSwitch-switchBase": {
                padding: 0,
                margin: 0,
                height: "100%",
                transitionDuration: "300ms",
                left: "2px",
                "&.Mui-checked": {
                  transform: "translateX(100%)",
                  color: "#fff",
                  left: "2px",
                  "& + .MuiSwitch-track": {
                    backgroundColor: "#1D4ED8",
                    opacity: 1,
                    border: 0,
                  },
                  "&.Mui-disabled + .MuiSwitch-track": {
                    opacity: 0.5,
                  },
                },
                "&.Mui-focusVisible .MuiSwitch-thumb": {
                  color: "#33cf4d",
                  border: "6px solid #fff",
                },
                "&.Mui-disabled .MuiSwitch-thumb": {
                  color: "#fff",
                },
                "&.Mui-disabled + .MuiSwitch-track": {
                  opacity: 0.7,
                },
              },
              "& .MuiSwitch-thumb": {
                width: 16,
                height: 16,
              },
              "& .MuiSwitch-track": {
                borderRadius: 50,
                backgroundColor: "#DCDCDE",
                opacity: 1,
              },
            },
          },
        },
        MuiTabs: {
          styleOverrides: {
            indicator: {
              backgroundColor: "#1D4ED8",
            },
          },
        },
        MuiTab: {
          styleOverrides: {
            root: {
              fontWeight: 400,
              fontSize: "14px",
              textTransform: "none",
              color: "#4F4D55",
              "&.Mui-selected": {
                color: "#1D4ED8",
              },
            },
          },
        },
        MuiDataGrid: {
          styleOverrides: {
            root: {
              border: "none !important",
              backgroundColor: "white",
              borderRadius: "15px",
            },
            row: {
              "&:last-child": {
                border: "none !important",
                borderBottom: "none !important",
              },
              "&.dashed-border-row": {
                borderBottom: "2px dashed #B7BAC1",
              },
            },
            cell: {
              fontSize: "14px",
              color: "#708090",
              fontWeight: 500,
              fontFamily: "Lexend",
              border: "none !important",
              borderBottom: "none !important",
            },
            columnHeaders: {
              fontSize: "16px",
              fontWeight: 600,
              color: "#ffffff",
              lineHeight: "20px",
              fontFamily: "Lexend",
              borderRadius: "15px",
              border: "none !important",
              borderBottom: "none !important",
              borderBottomLeftRadius: "15px",
              borderBottomRightRadius: "15px",
            },
            columnHeader: {
              fontWeight: 600,
              fontFamily: "Lexend",
              lineHeight: "20px",
              borderBottom: "none !important",
            },
            columnHeaderTitle: {
              fontWeight: 600,
              fontFamily: "Lexend",
              lineHeight: "20px",
            },
            columnHeadersInner: {
              borderRadius: "15px",
              border: "none !important",
              borderBottom: "none !important",
            },
            columnHeaderRow: {
              backgroundColor: "#E7EFF4 !important",
              color: "#000",
              borderRadius: "15px",
              borderBottomLeftRadius: "0px",
              borderBottomRightRadius: "0px",
              borderBottom: "none !important",
            },
            iconSeparator: {
              display: "none",
            },
            main: {
              border: "none !important",
            },
          },
        },

        MuiChip: {
          styleOverrides: {
            root: {
              padding: "5px 10px",
              "& .MuiChip-label": {
                padding: "0px",
              },
            },
          },
        },

        MuiPagination: {
          styleOverrides: {
            root: {
              ".MuiPagination-ul": {
                justifyContent: "end",
              },
              ".Mui-selected": {
                color: "#fff",
                backgroundColor: "#194774 !important",
              },
            },
          },
        },
        MuiPickersDay: {
          styleOverrides: {
            // today: {
            //   "&:not(.Mui-selected)": {
            //     border: "none",
            //     backgroundColor: "#1D4ED8",
            //     color: "#FFFFFF",
            //   },
            // },

            root: {
              width: "30px",
              height: "30px",
              color: "#1C1C1C",
              "&.Mui-selected": {
                backgroundColor: "#2563EB",
                color: "#FFFFFF",
              },

              "&:hover": {
                backgroundColor: "#2563EB",
                color: "#FFFFFF",
              },
            },
          },
        },
        MuiDayCalendar: {
          styleOverrides: {
            weekDayLabel: {
              color: "#BBBABD",
            },
          },
        },
        MuiPickersCalendarHeader: {
          styleOverrides: {
            label: {
              color: "#1C1C1C",
            },
          },
        },
        MuiPaper: {
          styleOverrides: {
            root: {
              "&.MuiPickersPopper-paper": {
                color: "#1C1C1C",
              },
            },
          },
        },
      },
    });

    // If animations should be disabled, override transition durations & ripple
    if (disableAnimation) {
      baseTheme = createTheme(baseTheme, {
        transitions: {
          duration: {
            shortest: 0,
            shorter: 0,
            short: 0,
            standard: 0,
            complex: 0,
            enteringScreen: 0,
            leavingScreen: 0,
          },
        },
        components: {
          MuiButtonBase: {
            defaultProps: {
              disableRipple: true,
            },
          },
        },
      });
    }

    return baseTheme;
  }, [fontSize, disableAnimation]);
  return <ThemeProvider theme={theme}>{children}</ThemeProvider>;
}
